import ApiService from '@/core/services/api.service';
import moment from 'moment';

const PATH_ENTRANCE_STUDENT_RESULT = 'academic/marking/exam-retake/entrance';
const PATH_ENTRANCE_STUDENT_RESULT_EXPORT = 'academic/marking/exam-retake/entrance/export';
const PATH_ENTRANCE_STUDENT_RESULT_LOG = 'academic/marking/exam-retake/entrance/log';

export const ENTRANCE_STUDENT_RESULT_LIST = 'entrance-student-result-list';
export const ENTRANCE_STUDENT_RESULT_HISTORY = 'entrance-student-result-history';
export const ENTRANCE_STUDENT_RESULT_EXPORT = 'entrance-student-result-export';
export const ENTRANCE_STUDENT_RESULT_CREATE = 'entrance-student-result-create';
export const ENTRANCE_STUDENT_RESULT_UPDATE = 'entrance-student-result-update';

function parseScoreDetail(scoreDetail) {
  const scores = {
    listening: null,
    reading: null,
    writing: null,
    speaking: null,
    vocab: null,
  };

  if (scoreDetail == null || scoreDetail === '') {
    return scores;
  }

  let detail = scoreDetail;
  if (typeof detail === 'string') {
    try {
      detail = JSON.parse(detail);
    } catch {
      return scores;
    }
  }

  if (typeof detail !== 'object' || Array.isArray(detail)) {
    return scores;
  }

  scores.listening = detail.listening_score ?? null;
  scores.reading = detail.reading_score ?? null;
  scores.writing = detail.writing_score ?? null;
  scores.speaking = detail.speaking_score ?? null;
  scores.vocab = detail.vocabulary_and_grammar_score ?? detail.vocab_score ?? null;

  return scores;
}

function mapEntranceStudentResultItem(item = {}) {
  const scores = parseScoreDetail(item.score_detail);

  return {
    id: item.id,
    user_id: item.user_id,
    student_name: item.user_name || '',
    phone: item.user_phone || '',
    listening: scores.listening,
    reading: scores.reading,
    writing: scores.writing,
    speaking: scores.speaking,
    vocab: scores.vocab,
    overall: item.avg_score ?? null,
    exam_time: item.result_at,
    created_at: item.created_at,
    exam_type: item.exam_type || '',
  };
}

function mapEntranceHistoryItem(item = {}) {
  const scores = parseScoreDetail(item.score_detail);

  return {
    id: item.id,
    listening: scores.listening,
    reading: scores.reading,
    writing: scores.writing,
    speaking: scores.speaking,
    overall: item.avg_score ?? null,
    updated_at: item.updated_at || item.created_at,
    updated_by_name: item.user_update?.name || '',
    updated_by_code: (item.user_update?.id ?? item.user_update_id) + 100000 ?? null,
  };
}

function mapEntranceStudentResultForExport(item) {
  const mapped = mapEntranceStudentResultItem(item);

  return {
    student_name: mapped.student_name,
    phone: mapped.phone,
    listening: mapped.listening ?? '',
    reading: mapped.reading ?? '',
    writing: mapped.writing ?? '',
    speaking: mapped.speaking ?? '',
    overall: mapped.overall ?? '-',
    exam_time: mapped.exam_time ? moment(mapped.exam_time).format('HH:mm:ss DD/MM/YYYY') : '',
    created_at: mapped.created_at ? moment(mapped.created_at).format('HH:mm:ss DD/MM/YYYY') : '',
  };
}

function buildEntranceFilterParams(payload = {}) {
  const params = {};

  if (payload.student_keyword?.trim()) {
    params.keyword = payload.student_keyword.trim();
  }

  if (Array.isArray(payload.exam_times) && (payload.exam_times[0] || payload.exam_times[1])) {
    params.result_at_range = payload.exam_times;
  }

  if (Array.isArray(payload.created_ats) && (payload.created_ats[0] || payload.created_ats[1])) {
    params.created_at_range = payload.created_ats;
  }

  return params;
}

function buildEntranceListParams(payload = {}) {
  return {
    page: payload.page || 1,
    per_page: payload.per_page || 20,
    ...buildEntranceFilterParams(payload),
  };
}

function appendScoreFields(body, payload = {}) {
  const scoreFields = [
    ['listening_score', payload.listening_score],
    ['reading_score', payload.reading_score],
    ['writing_score', payload.writing_score],
    ['speaking_score', payload.speaking_score],
  ];

  scoreFields.forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      body[key] = Number(value);
    }
  });

  return body;
}

function buildEntranceCreatePayload(payload = {}) {
  return appendScoreFields(
    {
      user_id: payload.user_id,
      result_at: payload.result_at,
    },
    payload
  );
}

function buildEntranceUpdatePayload(payload = {}) {
  return appendScoreFields(
    {
      marking_exam_result_id: payload.marking_exam_result_id,
      user_id: payload.user_id,
    },
    payload
  );
}

const actions = {
  [ENTRANCE_STUDENT_RESULT_LIST](context, payload) {
    return new Promise((resolve, reject) => {
      ApiService.query(PATH_ENTRANCE_STUDENT_RESULT, buildEntranceListParams(payload))
        .then(({ data }) => {
          resolve({
            ...data,
            data: (data.data || []).map(mapEntranceStudentResultItem),
          });
        })
        .catch(({ response }) => {
          reject(response?.data || response);
        });
    });
  },
  [ENTRANCE_STUDENT_RESULT_HISTORY](context, markingExamResultId) {
    return new Promise((resolve, reject) => {
      ApiService.query(`${PATH_ENTRANCE_STUDENT_RESULT_LOG}/${markingExamResultId}`)
        .then(({ data }) => {
          const paginated = data.data || {};

          resolve({
            error: data.error,
            message: data.message,
            data: (paginated.data || []).map(mapEntranceHistoryItem),
          });
        })
        .catch(({ response }) => {
          reject(response?.data || response);
        });
    });
  },
  [ENTRANCE_STUDENT_RESULT_EXPORT](context, payload) {
    return new Promise((resolve, reject) => {
      ApiService.query(PATH_ENTRANCE_STUDENT_RESULT_EXPORT, buildEntranceFilterParams(payload))
        .then(({ data }) => {
          resolve({
            ...data,
            data: (data.data || []).map(mapEntranceStudentResultForExport),
          });
        })
        .catch(({ response }) => {
          reject(response?.data || response);
        });
    });
  },
  [ENTRANCE_STUDENT_RESULT_CREATE](context, payload) {
    return new Promise((resolve, reject) => {
      ApiService.post(PATH_ENTRANCE_STUDENT_RESULT, buildEntranceCreatePayload(payload))
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          reject(response?.data || response);
        });
    });
  },
  [ENTRANCE_STUDENT_RESULT_UPDATE](context, payload) {
    return new Promise((resolve, reject) => {
      ApiService.put(PATH_ENTRANCE_STUDENT_RESULT, buildEntranceUpdatePayload(payload))
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          reject(response?.data || response);
        });
    });
  },
};

export default {
  namespaced: false,
  state: {},
  getters: {},
  actions,
  mutations: {},
};
